import { Link as RouterLink, generatePath } from 'react-router';

import { RefinancingHero } from '@/components/RefinancingHero/index.ts';
import { Button } from '@/ui/button-2.tsx';

import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import useBrainSession from '../../services/BrainSession/useBrainSession.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_SAVE_OR_REPAY_LOAN } from './RRS.ts';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const rowClassName = 'mx-auto flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row';

const debtCheckButtonClassName = 'max-w-[550px] self-center sm:min-w-[330px]';

const SaveOrRepayLoan: React.FC = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <Page title={appStrings.MENU.SAVE_OR_REPAY_LOAN}>
      <RrsHandler {...RRS_SAVE_OR_REPAY_LOAN} />
      <RefinancingHero chatStep="refin_present_terms_auto_new" />
      <Section>
        <div className="flex max-w-[900px]! flex-col">
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Lønner det seg å spare eller nedbetale lån?
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Det er alltid smart å ha penger i bakhånd, slik at du ikke behøver å bruke kredittkortet
            eller ta opp et lån når det kommer uforutsette utgifter. Men hvor mye penger bør du ha,
            og bør du prioritere å spare eller betale på lånene dine? Og hvilke lån bør du nedbetale
            først?
          </h2>
          <p className="typography-v1-legacy-subtitle2">
            At det lønner seg å ha gjeld og lån er en gammel myte. Årsaken til at vi ofte tror
            dette, er at vi får et skattefradrag på 22 prosent. Det vi gjerne ikke tenker på, er at
            vi fremdeles må betale 78 prosent av rentekostnadene – og det utgjør mye penger for de
            fleste av oss. Fradraget i seg selv er med andre ord ikke en god grunn til å ikke
            nedbetale lånene dine.
          </p>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={rowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Derfor lønner det seg å nedbetale lån fremfor å spare
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Som en tommelfingerregel er det aldri lønnsomt å ha lån, enkelt og greit fordi du må
              betale renter. Og rentene på lån er alltid høyere enn du får på pengene du har stående
              i banken.
              <br />
              <br />
              Jo høyere rente, desto større blir differansen – derfor lønner det seg å bli kvitt
              kredittkortgjeld og forbrukslån før du begynner å spare til buffer. Et annet godt råd
              er å betale ned billånet, og deretter på boliglånet til du får en sikkerhet innenfor
              50 prosent. Studielånet venter du med til slutt.
              <br />
              <br />
              I tillegg har vi høy inflasjon og lave renter for tiden, noe som gjør at du rett og
              slett taper på å ha penger i banken. Et enkelt regneeksempel viser at dersom du har en
              høyrentekonto med 100 000 kroner til 1 prosent rente, og inflasjonen er 2,5 prosent –
              så taper du faktisk 1500 kroner i året.
              <br />
              <br />
              <RouterLink className={linkClassName} to={routes.REPAYMENT_PERIOD}>
                Kort eller lang nedbetalingstid – hva lønner seg for deg?
              </RouterLink>
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Kan det lønne seg å spare fremfor å nedbetale lån?
            </h3>
            <p className="typography-v1-legacy-p3">
              Ingen regel uten unntak, og i første rekke gjelder dette hvis du ikke allerede har en
              buffer – og ikke har kredittkortgjeld og forbrukslån. Men hvor mye bør den være på?
              Her er det heller ingen regler da det blant annet avhenger av alder, inntekt og hva du
              eier.
              <br />
              <br />
              Noen retningslinjer på bufferen kan det likevel være greit å ha:
            </p>
            <ul className="typography-v1-legacy-list typography-v1-legacy-p3">
              <li>Student som leier: 5000 kroner</li>
              <li>Ung med jobb og boliglån: 15.000 kroner</li>
              <li>Enslig med jobb og boliglån: 20.000 kroner</li>
              <li>Enslig med jobb, barn og boliglån: 25.000 kroner</li>
              <li>Par med jobb, barn og boliglån: 30.000 kroner</li>
            </ul>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Et annet argument for å spare er hvis du klarer å få bedre avkastning på pengene dine
              enn du betaler i renter på lånene dine, etter fradrag og inflasjon. En måte du kan få
              til dette på, er å spare i aksjefond. Med det sagt – da skal du kjenne markedet godt,
              og det er få om noen fond som vil gi deg bedre avkastning enn 20-25 prosent hvert
              eneste år. Dessuten må du tenke svært langsiktig.
              <br />
              <br />
              <strong>
                Det lønner seg altså å nedbetale kredittkort og forbrukslån med høy rente, men hva
                om økonomien din gjør at du ikke har råd til dette? Svaret på det kan være å{' '}
                <RouterLink className={linkClassName} to={routes.PRODUCTS_REFINANCING}>
                  refinansiere
                </RouterLink>{' '}
                – da kan du spare tusenvis av kroner hver eneste måned, slik at du raskere får
                nedbetalt gjelden og bygget opp bufferen din.
              </strong>
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Spar penger på refinansiering hos Scoopr
            </h3>
            <p className="typography-v1-legacy-p3">
              I løpet av to minutter søker du enkelt, gratis og uforpliktende om{' '}
              <RouterLink className={linkClassName} to={routes.PRODUCTS_REFINANCING}>
                refinansiering fra 20.000 til 500.000 kroner
              </RouterLink>{' '}
              hos sammenligningstjenesten Scoopr.
              <br />
              <br />
              <strong>
                Finn frem BankID-en din,{' '}
                <RouterLink
                  className={linkClassName}
                  to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                    articleName: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
                  })}
                >
                  trykk her
                </RouterLink>
                , fyll inn i skjemaet – og så er du ferdig!
              </strong>
              <br />
              <br />
              Fordelene med å bruke Scoopr til refinansiering er:
            </p>
            <ul className="typography-v1-legacy-list typography-v1-legacy-p3">
              <li>
                Søknaden din blir automatisk sendt til 13 banker som alle konkurrerer om å gi deg så
                lav rente som mulig.
              </li>
              <li>Du sparer tid fordi du slipper å sende flere søknader til mange banker.</li>
              <li>
                Du sparer penger fordi vi samarbeider med banker vi vet vil gi deg gode tilbud.
              </li>
              <li>
                Du får svar i løpet av kort tid, og du har 30 dager på deg til å vurdere hvilken
                bank du velger.
              </li>
              <li>
                Som regel betaler banken kredittkortene og forbrukslånene til kreditorene dine for
                deg – du kan med andre ord være sikker på at du blir kvitt gjelden din for godt.
              </li>
            </ul>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              <RouterLink className={linkClassName} to={routes.PRODUCTS_REFINANCING}>
                Her finner du ut hvor mye kan du spare på å refinansiere hos Scoopr
              </RouterLink>
              <br />
              <br />
              <strong>
                Et lite tips til slutt: Dersom du ønsker å spare til buffer, vær realistisk på hvor
                mye du klarer å sette av hver måned slik at du holder motivasjonen oppe. Husk også
                at alt er bedre enn ingenting – om det ikke ble så mye denne måneden, kanskje du
                klarer å spare mer den neste?
              </strong>
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <Button
            color="yellow"
            className={debtCheckButtonClassName}
            onClick={handleDebtCheckClick}
          >
            Sjekk nå
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default SaveOrRepayLoan;
